import { useEffect, useRef } from "react"
import LoadingButton from "../../../../../components/LoadingButton"

// A spark body that is empty or just a "# title" heading isn't a real transcript.
const isPlaceholderBody = (v) => {
    const b = (v ?? "").trim()
    return b.length === 0 || (b.length < 40 && b.startsWith("#"))
}

// Show, field by field, what a proposed change would do: edit = before/after, add = new only,
// remove = old only (struck through). The long transcript field stays behind a disclosure.
export const renderDiff = (diff) => {
    if (!diff) return null
    const op = diff.op ?? "edit"
    const before = diff.before ?? {}
    const after = diff.after ?? {}
    const noun = diff.itemType === "spark" ? "spark" : diff.itemType === "chapter" ? "chapter" : "activity"
    const labels = Object.keys(op === "remove" ? before : after).filter(l => {
        const a = (before[l] ?? "").trim()
        const b = (after[l] ?? "").trim()
        if (op === "add") return b.length > 0
        if (op === "remove") return a.length > 0
        if (a === b) return false
        if (l === "Transcript" && isPlaceholderBody(a) && isPlaceholderBody(b)) return false
        return true
    })
    if (op === "edit" && !labels.length) return <p className="card-label neutral">The AI returned the same content, so there's nothing to change here. Skip or decline, or edit this item directly above.</p>
    const heading = op === "add" ? `Will add ${noun}` : op === "remove" ? `Will remove ${noun}` : "Proposed change"
    const bg = op === "remove" ? "rgba(179, 38, 30, 0.06)" : "rgba(26, 127, 68, 0.06)"
    return (
        <div className="flex columns gap-xs" style={{ background: bg, borderRadius: 8, padding: "8px 12px" }}>
            <p className="card-label medium">{heading}</p>
            {labels.map(l => (
                <div key={l} className="flex columns gap-xxs">
                    <p className="card-label neutral" style={{ textTransform: "uppercase", letterSpacing: 0.4 }}>{l}</p>
                    {l === "Transcript"
                        ? <details>
                            <summary className="card-label" style={{ cursor: "pointer" }}>Transcript {op === "add" ? "to write" : op === "remove" ? "to remove" : "to rewrite"} — view content</summary>
                            <div className="flex columns gap-xxs" style={{ marginTop: 6 }}>
                                {op !== "add" && <>
                                    <p className="card-label neutral">{op === "remove" ? "Removing" : "Before"}</p>
                                    <pre className="card-label neutral" style={{ whiteSpace: "pre-wrap", margin: 0, ...(op === "remove" ? { textDecoration: "line-through" } : {}) }}>{before[l] || "(empty)"}</pre>
                                </>}
                                {op !== "remove" && <>
                                    <p className="card-label neutral">{op === "add" ? "New" : "After"}</p>
                                    <pre className="card-body" style={{ whiteSpace: "pre-wrap", margin: 0 }}>{after[l]}</pre>
                                </>}
                            </div>
                        </details>
                        : <>
                            {op !== "add" && before[l] && <p className="card-body neutral" style={{ textDecoration: "line-through" }}>{before[l]}</p>}
                            {op !== "remove" && after[l] && <p className="card-body" style={{ background: "rgba(26, 127, 68, 0.14)", borderRadius: 4, padding: "2px 6px" }}>{after[l]}</p>}
                        </>}
                </div>
            ))}
        </div>
    )
}

// The Cursor-style inline diff card, rendered AT the targeted chapter/activity/spark in the editor.
// Shows the auto-highlighted before/after of the active change, with Accept / Decline / Skip.
export function InlineChange({ change, onAccept, onDecline, onSkip, onRetry }) {
    const { op, label, badgeColor, area, problem, fix, proposal } = change
    const state = proposal?.state ?? "loading"
    // Auto-navigate: when this becomes the active change, bring it into view so the author lands on
    // the next thing to review without scrolling. Keyed on change.id so it fires once per change.
    const ref = useRef(null)
    useEffect(() => {
        if (ref.current && ref.current.scrollIntoView) ref.current.scrollIntoView({ behavior: "smooth", block: "center" })
    }, [change.id])
    return (
        <div
            ref={ref}
            className="flex columns gap-xs showing"
            style={{ border: `1px solid ${badgeColor}40`, borderLeft: `3px solid ${badgeColor}`, borderRadius: 8, padding: "10px 12px", background: "rgba(103, 80, 164, 0.03)" }}
        >
            <div className="flex space-between center gap-xs wrap">
                <div className="flex center gap-xs wrap">
                    <span className="material-symbols-outlined scale-xs" style={{ color: badgeColor }}>auto_awesome</span>
                    <span className="card-label medium" style={{ padding: "1px 8px", borderRadius: 999, color: "#fff", background: badgeColor }}>{label}</span>
                    {area && <p className="card-label neutral" style={{ textTransform: "capitalize" }}>{area}</p>}
                </div>
            </div>

            {problem && <p className="card-body">{problem}</p>}

            {state === "loading" && <p className="card-label neutral">Generating proposed change…</p>}
            {state === "loading" && fix && (
                <div className="flex columns gap-xxs" style={{ background: "rgba(103, 80, 164, 0.06)", borderRadius: 8, padding: "8px 12px" }}>
                    <p className="card-label medium">Suggested fix</p>
                    <p className="card-body">{fix}</p>
                </div>
            )}
            {state === "error" && <p className="card-label error">{proposal?.error || "Couldn't generate this change."}</p>}
            {state === "ready" && renderDiff(proposal.diff)}

            <div className="flex justify-end center gap-xs vi-spacer-xs">
                <button className="text small" onClick={onSkip}>Skip</button>
                <button className="text small" onClick={onDecline}>Decline</button>
                {state === "error"
                    ? <button className="outline small with-icon leading" onClick={onRetry}>
                        <span className="material-symbols-outlined scale-xs">refresh</span><span>Retry</span>
                    </button>
                    : <button className="solid small with-icon leading" disabled={state !== "ready"} onClick={onAccept}>
                        <span className="material-symbols-outlined scale-xs">{op === "remove" ? "delete" : "check"}</span><span>Accept</span>
                    </button>}
            </div>
        </div>
    )
}

// "Review & improve with AI" controller. Owns the Validate button, the overall verdict, queue
// progress, the manual-attention list and the completion summary. The per-change diff + Accept/
// Decline/Skip render inline in the chapters above (see ChaptersContent → InlineChange), driven by
// the same `review` hook passed in here.
export default function QaReviewPanel({ review }) {
    const { validate, validating, error, hasReport, active, total, counts, reviewedCount, skippedRemaining, manualIssues, done, reviewSkipped, declineSkipped } = review

    return (
        <div className="flex columns gap-s">
            <div className="bt-ai-banner">
                <span className="material-symbols-outlined bt-ai-banner-icon">fact_check</span>
                <div className="flex columns gap-xxs bt-ai-banner-copy">
                    <p className="card-body medium">Review &amp; improve with AI</p>
                    <p className="card-label neutral">Check progression, coverage, duplication, identity, outcome and evidence, then accept, decline or skip each change one at a time — right where it lives.</p>
                </div>
                <LoadingButton className="solid" label={hasReport ? "Re-validate track" : "Validate track with AI"} leadingIcon="auto_awesome" loading={validating} onClick={validate} />
            </div>

            {hasReport && (
                <div className="flex columns gap-s showing">
                    {/* Active change is being reviewed inline, above. */}
                    {active && (
                        <div className="bt-publish-banner" style={{ background: "rgba(103, 80, 164, 0.08)" }}>
                            <span className="material-symbols-outlined">arrow_upward</span>
                            <p className="card-body">
                                {reviewedCount} of {total} reviewed{skippedRemaining ? ` · ${skippedRemaining} skipped` : ""} — the highlighted change above is waiting for you.
                            </p>
                        </div>
                    )}

                    {/* All applicable changes resolved. */}
                    {done && (
                        <div className="flex columns gap-xs">
                            <p className="card-body medium">All changes reviewed.</p>
                            <p className="card-body neutral">
                                Applied {counts.accepted} · Declined {counts.declined}
                                {counts.skipped ? ` · Skipped ${counts.skipped}` : ""}
                                {counts.stale ? ` · Needs re-validation ${counts.stale}` : ""}.
                            </p>
                            {skippedRemaining > 0 && (
                                <div className="flex center gap-xs vi-spacer-xs">
                                    <button className="outline small" onClick={reviewSkipped}>Review {skippedRemaining} skipped</button>
                                    <button className="text small" onClick={declineSkipped}>Decline {skippedRemaining} skipped</button>
                                </div>
                            )}
                        </div>
                    )}

                    {total === 0 && manualIssues.length === 0 && (
                        <p className="card-body neutral">No issues found.</p>
                    )}
                    {total === 0 && manualIssues.length > 0 && (
                        <p className="card-body neutral">No one-click changes — see what needs your attention below.</p>
                    )}

                    {manualIssues.length > 0 && (
                        <div className="flex columns gap-xs">
                            <p className="card-body medium">Needs your attention</p>
                            <p className="card-label neutral">These are cross-cutting and can't be auto-applied. Fix them by hand in the chapters above, then re-validate.</p>
                            {manualIssues.map((it, i) => (
                                <div key={i} className="bt-review-chapter flex columns gap-xxs">
                                    <p className="card-label medium" style={{ textTransform: "capitalize" }}>
                                        {it.area}{it.chapter && it.chapter !== "n/a" ? ` · ${it.chapter}` : ""}
                                    </p>
                                    <p className="card-body">{it.problem}</p>
                                    {it.fix && (
                                        <div className="flex columns gap-xxs" style={{ background: "rgba(103, 80, 164, 0.06)", borderRadius: 8, padding: "8px 12px" }}>
                                            <p className="card-label medium">Suggested fix</p>
                                            <p className="card-body">{it.fix}</p>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {error && <p className="card-label error showing">{error}</p>}
        </div>
    )
}
